import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useUserStore } from '../store/useUserStore';
import { useSubscriptionModalStore } from '../store/useSubscriptionModalStore';
import i18n from './i18n';
import { getMessageApi } from './messageClient';
import { trackEvent } from './analytics';
import { EVENTS } from '@/utils/analyticsEvents';

// 需要升级订阅的错误码
const SUBSCRIPTION_REQUIRED_CODE = 20001;

// 扩展 Axios 配置类型
declare module 'axios' {
  // 用户可传入的配置项（InternalAxiosRequestConfig 会自动继承）
  export interface AxiosRequestConfig {
    hideMessageModal?: boolean; // 隐藏错误提示
    skipUpgradeModal?: boolean; // 跳过升级订阅弹框
  }
  // 拦截器内部使用的额外属性
  export interface InternalAxiosRequestConfig {
    _retry?: boolean; // 标记是否已重试过（用于 token 刷新）
  }
}

const notifyError = (content: string): void => {
  const messageApi = getMessageApi();
  if (messageApi) {
    messageApi.error(content);
  } else {
    console.error(content);
  }
};

const getFeatureFromUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (url.includes('/analysis/cv')) return 'cv_analysis';
  if (url.includes('/analysis/cl')) return 'cl_analysis';
  if (url.includes('/cl-generation')) return 'cl_generation';
  if (url.includes('/interview')) return 'interview';
  return undefined;
};

const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10 * 1000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

const REFRESH_LOCK_KEY = 'auth:refresh-lock';
const REFRESH_LOCK_TTL = 15 * 1000;
const TAB_ID = (() => {
  const key = 'auth:tab-id';
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const newId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  sessionStorage.setItem(key, newId);
  return newId;
})();

type RefreshLock = {
  owner: string;
  expiresAt: number;
};

const getRefreshLock = (): RefreshLock | null => {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RefreshLock;
    if (!parsed?.expiresAt || !parsed?.owner) return null;
    if (Date.now() > parsed.expiresAt) return null;
    return parsed;
  } catch (error) {
    console.warn('[Auth Debug] Failed to parse refresh lock:', error);
    return null;
  }
};

const setRefreshLock = (): RefreshLock => {
  const lock: RefreshLock = { owner: TAB_ID, expiresAt: Date.now() + REFRESH_LOCK_TTL };
  localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
  return lock;
};

const clearRefreshLock = (): void => {
  const lock = getRefreshLock();
  if (lock?.owner === TAB_ID) {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }
};

const isAnotherTabRefreshing = (): boolean => {
  const lock = getRefreshLock();
  return !!lock && lock.owner !== TAB_ID;
};

const getPersistedTokens = (): { token: string; refreshToken: string } | null => {
  try {
    const raw = localStorage.getItem('userStore');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { token?: string; refreshToken?: string }; token?: string; refreshToken?: string };
    const state = parsed?.state ?? parsed;
    if (state?.token && state?.refreshToken) {
      return { token: state.token, refreshToken: state.refreshToken };
    }
    return null;
  } catch (error) {
    console.warn('[Auth Debug] Failed to parse userStore:', error);
    return null;
  }
};

const waitForTokenUpdate = (timeoutMs: number): Promise<string | null> =>
  new Promise((resolve) => {
    const currentTokens = getPersistedTokens();
    if (currentTokens?.token) {
      resolve(currentTokens.token);
      return;
    }

    let timeoutId: number | undefined;
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'userStore') return;
      const nextTokens = getPersistedTokens();
      if (nextTokens?.token) {
        cleanup();
        resolve(nextTokens.token);
      }
    };

    const cleanup = () => {
      window.removeEventListener('storage', handleStorage);
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    window.addEventListener('storage', handleStorage);
    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
  });

// 是否正在刷新 token
let isRefreshing = false;
// 是否正在重定向到登录页（防止重复跳转）
let isRedirecting = false;
// 待重试的请求队列
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

// 处理队列中的请求
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// 需要排除的认证相关路径（不应作为 from 参数）
const AUTH_PATHS = ['/login', '/register', '/verify-email', '/forgot-password', '/reset-password', '/google-callback'];

/**
 * 获取有效的 from 路径
 * 排除认证相关页面，避免登录后跳回登录页
 */
const getValidFromPath = (): string => {
  const currentPath = location.pathname;
  // 如果当前路径是认证相关页面，不保存为 from
  if (AUTH_PATHS.some((authPath) => currentPath.startsWith(authPath))) {
    return '/dashboard'; // 返回默认路径
  }
  return currentPath + location.search;
};

/**
 * 安全跳转到登录页（防止重复跳转）
 */
const redirectToLogin = (): void => {
  if (isRedirecting) return;

  isRedirecting = true;
  const from = getValidFromPath();

  // 使用 setTimeout 确保状态重置（页面跳转后）
  setTimeout(() => {
    isRedirecting = false;
  }, 1000);

  window.location.href = `/login?from=${encodeURIComponent(from)}`;
};

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const token = useUserStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      config.headers['X-Client-Timezone'] = timezone;
    }

    // 添加多语言支持
    // en: English (default)
    // fr: French (Français)
    const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    config.headers['Accept-Language'] = currentLanguage;

    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    const { data, status, config } = response;
    const successCodes = [200, 201, 202, 204];

    if (!successCodes.includes(status)) {
      if (!config.hideMessageModal) {
        notifyError(response.data.message);
      }

      return Promise.reject(response);
    } else {
      // 对于 blob 响应，直接返回（不检查 code）
      if (config.responseType === 'blob') {
        return Promise.resolve(response);
      }

      if (!data || response.status === 204) return Promise.resolve(response);
      const { code } = data;

      if (!successCodes.includes(code)) {
        if (!config.hideMessageModal) {
          notifyError(data.message);
        }
        return Promise.reject(response);
      }
      return Promise.resolve(response);
    }
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error?.response?.status;
    const responseData = error?.response?.data as { code?: number; message?: string } | undefined;
    const endpoint = originalRequest?.url;
    const feature = getFeatureFromUrl(endpoint);
    const timeoutSeconds = error.config?.timeout ? Math.round(error.config.timeout / 1000) : undefined;

    if (status === 204) {
      return Promise.resolve(error.response);
    }

    // 处理 400 + code=20001 - 需要升级订阅
    if (status === 400 && responseData?.code === SUBSCRIPTION_REQUIRED_CODE) {
      if (!originalRequest?.skipUpgradeModal) {
        trackEvent(EVENTS.QUOTA_LIMIT_REACHED, { feature, plan: 'freemium' });
        useSubscriptionModalStore.getState().showModal({ feature, context: 'quota_limit' });
      }
      return Promise.reject(error);
    }

    if (error.code === 'ECONNABORTED') {
      trackEvent(EVENTS.TIMEOUT_OCCURRED, {
        feature,
        timeout_duration_seconds: timeoutSeconds,
      });
    }

    // 处理 401 错误 - Token 过期
    if (status === 401 && originalRequest && !originalRequest._retry) {
      const currentRefreshToken = useUserStore.getState().refreshToken;
      console.warn('[Auth Debug] 401 detected:', {
        url: originalRequest.url,
        refreshToken: currentRefreshToken ? 'exists' : 'empty',
        isRefreshing,
        responseData: responseData,
      });

      if (isRefreshing) {
        // 如果正在刷新 token，将请求加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return service(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      if (isAnotherTabRefreshing()) {
        const tokenFromOtherTab = await waitForTokenUpdate(REFRESH_LOCK_TTL);
        if (tokenFromOtherTab) {
          originalRequest._retry = true;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${tokenFromOtherTab}`;
          }
          return service(originalRequest);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;
      setRefreshLock();

      const refreshToken = useUserStore.getState().refreshToken;
      const accessToken = useUserStore.getState().token;

      if (!refreshToken) {
        // 没有 refresh token，直接登出
        console.error('[Auth Debug] No refresh token, logging out');
        isRefreshing = false;
        clearRefreshLock();
        processQueue(error, null);
        const errorMessage = (error?.response?.data as { message?: string })?.message || 'Session expired';
        notifyError(errorMessage);
        useUserStore.getState().clearAuth();
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        // 调用 refresh token API
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/v1/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: {
              'Content-Type': 'application/json;charset=UTF-8',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
            timeout: 10 * 1000,
          }
        );

        if (response.data.code === 200) {
          const { access_token, refresh_token: newRefreshToken } = response.data.data || {};
          if (!access_token) {
            throw new Error('Refresh token response missing access_token');
          }

          // 如果后端没有返回新的 refresh_token，保留原有的
          const finalRefreshToken = newRefreshToken || refreshToken;

          // 更新 tokens
          useUserStore.getState().setTokens(access_token, finalRefreshToken);

          // 更新原请求的 Authorization
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }

          // 处理队列中的请求
          processQueue(null, access_token);
          isRefreshing = false;
          clearRefreshLock();

          // 重试原请求
          return service(originalRequest);
        }
        throw new Error('Refresh token failed with non-200 code');
      } catch (refreshError) {
        // Refresh token 也过期了，登出
        const axiosErr = refreshError as AxiosError;
        console.error('[Auth Debug] Refresh token failed:', {
          message: axiosErr.message,
          status: axiosErr.response?.status,
          responseData: axiosErr.response?.data,
          code: axiosErr.code,
        });
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        clearRefreshLock();
        notifyError('Session expired, please login again');
        useUserStore.getState().clearAuth();
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    // 其他错误处理
    if (!error?.config?.hideMessageModal && error.name !== 'CanceledError' && error.name !== 'AbortError') {
      const errorMessage = responseData?.message || 'An error occurred';
      notifyError(errorMessage);
      trackEvent(EVENTS.API_ERROR_OCCURRED, {
        endpoint,
        status_code: status,
        error_message: errorMessage,
      });
      trackEvent(EVENTS.ERROR_DISPLAYED, {
        error_type: 'api',
        error_message: errorMessage,
        page: location.pathname,
        feature,
      });
    }
    return Promise.reject(error);
  }
);

export default service;
